// src/pages/reportes.js
//
// Reportes analíticos nativos (UPG-4). Sección Finanzas → Reportes dentro
// de LSSI Fin, sin dependencia de Tableau.
//
// Sesión 5 · 3-Sep · tres tandas construidas hoy:
//   Tanda 1: Producto, Modalidad, Geografía
//   Tanda 2: Clientes, Mes a Mes, Margen y Alertas
//   Tanda 3: Instructores, Partners, Monedas, Año vs Año, Nuevos vs Repetidores
//
// Real vs Presupuesto queda deferido: requiere modelo de presupuesto que
// aún no existe y decisión sobre cómo capturarlo.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink, Spinner, Alert, Badge, Input, Button, Label } from "reactstrap";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  API_URL_BI_PROJECTS,
  API_URL_BI_CURRENCY_EXPOSURE,
  API_URL_BI_WEEKLY_SUMMARY,
  API_URL_BI_WEEKLY_ANALYSIS,
  API_URL_instructores,
  API_URL_utilidades_partners,
} from "../constants";
import { getAuthHeaders } from "../utils/auth";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler, ChartDataLabels
);

// ------------------------------- Filtros -------------------------------
// Fecha se aplica sobre inicio_date del proyecto (más significativo para el
// negocio que registration_date). Cuando falta inicio_date, cae a
// registration_date como fallback.
const DATE_PRESETS = [
  { id: "all",       label: "Todo el histórico" },
  { id: "last-30",   label: "Últimos 30 días" },
  { id: "last-90",   label: "Últimos 90 días" },
  { id: "ytd",       label: "Este año" },
  { id: "last-year", label: "Año anterior" },
  { id: "custom",    label: "Personalizado" },
];

// Devuelve {start, end} en Date, o null si es "all".
function resolveDateRange(preset, customStart, customEnd) {
  if (preset === "all") return null;
  const now = new Date();
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (preset === "last-30") return { start: day(new Date(now.getTime() - 30 * 864e5)), end: day(now) };
  if (preset === "last-90") return { start: day(new Date(now.getTime() - 90 * 864e5)), end: day(now) };
  if (preset === "ytd")     return { start: new Date(now.getFullYear(), 0, 1), end: day(now) };
  if (preset === "last-year") {
    const y = now.getFullYear() - 1;
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
  }
  if (preset === "custom") {
    const start = customStart ? new Date(customStart) : null;
    const end   = customEnd   ? new Date(customEnd)   : null;
    if (!start && !end) return null;
    return { start, end };
  }
  return null;
}

// Extrae valores únicos de una dimensión, ordenados alfabéticamente,
// filtrando vacíos.
function uniqueValues(items, extract) {
  const set = new Set();
  (items || []).forEach((x) => {
    const v = extract(x);
    if (v && String(v).trim()) set.add(String(v).trim());
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
}

// Aplica filtros a un proyecto. Devuelve true si pasa todos los filtros
// activos ("all" o "" significa sin filtro para esa dimensión).
function projectMatchesFilters(p, filters, dateRange) {
  if (dateRange) {
    const dateStr = p.inicio_date || p.registration_date;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    if (dateRange.start && d < dateRange.start) return false;
    if (dateRange.end && d > dateRange.end) return false;
  }
  if (filters.sede     !== "all" && (p.sede?.name || "") !== filters.sede)     return false;
  if (filters.pais     !== "all" && (p.pais     || "") !== filters.pais)       return false;
  if (filters.producto !== "all" && (p.producto_nuevo || "") !== filters.producto)   return false;
  if (filters.subproducto !== "all" && (p.subproducto || "") !== filters.subproducto) return false;
  if (filters.status   !== "all" && (p.status   || "") !== filters.status)     return false;
  if (filters.lider    !== "all" && (p.lider    || "") !== filters.lider)      return false;
  return true;
}

const REPORTS = [
  { id: "semanal",         label: "Semanal" },
  { id: "producto",        label: "Producto" },
  { id: "rentabilidad",    label: "Rentabilidad" },
  { id: "modalidad",       label: "Modalidad" },
  { id: "geografia",       label: "Geografía" },
  { id: "clientes",        label: "Clientes" },
  { id: "mes-a-mes",       label: "Mes a Mes" },
  { id: "margen-alertas",  label: "Margen y Alertas" },
  { id: "instructores",    label: "Instructores" },
  { id: "partners",        label: "Partners" },
  { id: "monedas",         label: "Monedas" },
  { id: "anio-vs-anio",    label: "Año vs Año" },
  { id: "repetidores",     label: "Nuevos vs Repetidores" },
];

const GEO_LEVELS = [
  { id: "sede",  label: "Value Stream (Sede)" },
  { id: "pais",  label: "País" },
  { id: "ciudad", label: "Ciudad" },
];

const MARGIN_ALERT_THRESHOLD = 20;
const TOP_N_CLIENTES = 15;
const TOP_N_INSTRUCTORES = 20;
const TOP_N_PARTNERS = 15;
const EXCLUDED_PARTNERS_FROM_RANKING = new Set(["LSSI", "LSSI Europa"]);

const formatUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatNum = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
const formatPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function aggregate(items, keyOf, valueOf = (p) => Number(p.financials?.total_revenue) || 0) {
  const map = new Map();
  (items || []).forEach((p) => {
    const rawKey = keyOf(p);
    const key = (rawKey && String(rawKey).trim()) || "Sin especificar";
    const value = valueOf(p);
    const bucket = map.get(key) || { label: key, value: 0, count: 0 };
    bucket.value += value;
    bucket.count += 1;
    map.set(key, bucket);
  });
  return Array.from(map.values()).sort((a, b) => b.value - a.value);
}

function bucketByMonth(projects, dateField = "inicio_date") {
  const buckets = new Map();
  (projects || []).forEach((p) => {
    const dateStr = p[dateField] || p.inicio_date || p.registration_date;
    if (!dateStr) return;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    const y = d.getFullYear();
    const m = d.getMonth();
    const monthKey = `${y}-${String(m + 1).padStart(2, "0")}`;
    const monthLabel = d.toLocaleString("es-MX", { month: "short", year: "numeric" }).replace(".", "");
    const b = buckets.get(monthKey) || { monthKey, monthLabel, revenue: 0, cost: 0, profit: 0, count: 0 };
    b.revenue += Number(p.financials?.total_revenue) || 0;
    b.cost += Number(p.financials?.total_expenses) || 0;
    b.profit += Number(p.financials?.net_profit) || 0;
    b.count += 1;
    buckets.set(monthKey, b);
  });
  return Array.from(buckets.values())
    .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
    .map((b) => ({ ...b, margin: b.revenue > 0 ? (b.profit / b.revenue) * 100 : 0 }));
}

const PALETTE = [
  "#dc3545", "#0d6efd", "#198754", "#ffc107", "#6f42c1",
  "#fd7e14", "#20c997", "#d63384", "#0dcaf0", "#adb5bd",
];

function buildBarData(rows, colorSeed = 0, seriesLabel = "Ingreso (USD)") {
  return {
    labels: rows.map((r) => r.label),
    datasets: [{
      label: seriesLabel,
      data: rows.map((r) => r.value),
      backgroundColor: rows.map((_, i) => PALETTE[(i + colorSeed) % PALETTE.length]),
      borderWidth: 0,
      borderRadius: 4,
    }],
  };
}

function chartOptions(total, valueFormatter = formatUSD) {
  return {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.raw || 0;
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
            return `${valueFormatter(value)} · ${pct}% del total`;
          },
        },
      },
      datalabels: {
        anchor: "end", align: "end",
        color: "#374151", font: { size: 11, weight: 600 },
        formatter: (v) => valueFormatter(v),
      },
    },
    layout: { padding: { right: 90 } },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => valueFormatter(v) } },
      y: { ticks: { font: { size: 12 } } },
    },
  };
}

function BreakdownTable({ rows, total, valueLabel = "Ingreso (USD)", valueFormatter = formatUSD, countLabel = "Proyectos" }) {
  return (
    <div style={{ overflowX: "auto", marginTop: 16 }}>
      <table className="table table-sm table-hover" style={{ fontSize: "0.9em" }}>
        <thead className="table-light">
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Categoría</th>
            <th className="text-end">{countLabel}</th>
            <th className="text-end">{valueLabel}</th>
            <th className="text-end" style={{ width: 90 }}>% del total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pct = total > 0 ? ((r.value / total) * 100).toFixed(1) : "0.0";
            return (
              <tr key={r.label}>
                <td className="text-muted">{i + 1}</td>
                <td><strong>{r.label}</strong></td>
                <td className="text-end">{r.count}</td>
                <td className="text-end">{valueFormatter(r.value)}</td>
                <td className="text-end">{pct}%</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="table-light">
            <td></td>
            <td><strong>Total</strong></td>
            <td className="text-end"><strong>{rows.reduce((a, r) => a + r.count, 0)}</strong></td>
            <td className="text-end"><strong>{valueFormatter(total)}</strong></td>
            <td className="text-end"><strong>100%</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function BarDashboard({ rows, total, subtitle, seed = 0, valueLabel, valueFormatter, countLabel, seriesLabel }) {
  if (!rows || rows.length === 0) {
    return (
      <Alert color="info" className="mt-3">
        Sin datos para este corte. Verifica que haya datos capturados para esta dimensión.
      </Alert>
    );
  }
  const chartHeight = Math.max(220, rows.length * 34 + 60);
  return (
    <>
      {subtitle && <div className="text-muted mb-2" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div style={{ height: chartHeight }}>
        <Bar data={buildBarData(rows, seed, seriesLabel)} options={chartOptions(total, valueFormatter)} />
      </div>
      <BreakdownTable
        rows={rows}
        total={total}
        valueLabel={valueLabel}
        valueFormatter={valueFormatter}
        countLabel={countLabel}
      />
    </>
  );
}

function TrendDashboard({ months, subtitle }) {
  if (!months || months.length === 0) {
    return (
      <Alert color="info" className="mt-3">
        No hay proyectos con fecha de inicio capturada — no podemos armar la línea de tiempo.
      </Alert>
    );
  }
  const data = {
    labels: months.map((m) => m.monthLabel),
    datasets: [
      { type: "bar", label: "Ingreso", data: months.map((m) => m.revenue), backgroundColor: "#0d6efd", borderRadius: 3, yAxisID: "y", order: 2 },
      { type: "bar", label: "Utilidad", data: months.map((m) => m.profit), backgroundColor: "#198754", borderRadius: 3, yAxisID: "y", order: 3 },
      { type: "line", label: "Margen %", data: months.map((m) => m.margin), borderColor: "#dc3545", backgroundColor: "#dc3545", tension: 0.3, yAxisID: "y1", order: 1, pointRadius: 4, pointHoverRadius: 6 },
    ],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ctx.dataset.label === "Margen %"
            ? `Margen: ${ctx.raw.toFixed(1)}%`
            : `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
        },
      },
    },
    scales: {
      y:  { type: "linear", position: "left",  beginAtZero: true, title: { display: true, text: "USD" },       ticks: { callback: (v) => formatUSD(v) } },
      y1: { type: "linear", position: "right",                    title: { display: true, text: "Margen %" }, grid: { drawOnChartArea: false }, ticks: { callback: (v) => `${v.toFixed(0)}%` } },
    },
  };
  return (
    <>
      {subtitle && <div className="text-muted mb-2" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div style={{ height: 360 }}><Line data={data} options={options} /></div>
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <table className="table table-sm table-hover" style={{ fontSize: "0.9em" }}>
          <thead className="table-light">
            <tr>
              <th>Mes</th>
              <th className="text-end">Proyectos</th>
              <th className="text-end">Ingreso</th>
              <th className="text-end">Utilidad</th>
              <th className="text-end" style={{ width: 90 }}>Margen</th>
            </tr>
          </thead>
          <tbody>
            {months.slice().reverse().map((m) => (
              <tr key={m.monthKey}>
                <td><strong>{m.monthLabel}</strong></td>
                <td className="text-end">{m.count}</td>
                <td className="text-end">{formatUSD(m.revenue)}</td>
                <td className="text-end" style={{ color: m.profit >= 0 ? "#198754" : "#dc3545" }}>{formatUSD(m.profit)}</td>
                <td className="text-end">{formatPct(m.margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AlertsDashboard({ projects, subtitle }) {
  const withMargin = (projects || [])
    .filter((p) => (p.financials?.total_revenue || 0) > 0)
    .map((p) => ({
      code: p.proyecto_code, name: p.name, cliente: p.cliente || "Sin cliente",
      lider: p.lider || "—", status: p.status || "—",
      revenue: p.financials?.total_revenue || 0,
      profit: p.financials?.net_profit || 0,
      margin: p.financials?.profit_margin || 0,
    }))
    .sort((a, b) => a.margin - b.margin);

  const negative = withMargin.filter((p) => p.margin < 0);
  const low = withMargin.filter((p) => p.margin >= 0 && p.margin < MARGIN_ALERT_THRESHOLD);

  if (withMargin.length === 0) {
    return <Alert color="info" className="mt-3">No hay proyectos con ingreso registrado para evaluar margen.</Alert>;
  }

  const renderRow = (p, kind) => (
    <tr key={p.code}>
      <td><Badge color={kind === "neg" ? "danger" : "warning"} className="me-2">{formatPct(p.margin)}</Badge></td>
      <td>
        <strong>{p.name}</strong>
        <div className="text-muted" style={{ fontSize: "0.8em" }}>{p.code} · {p.cliente} · Líder: {p.lider}</div>
      </td>
      <td className="text-end">{formatUSD(p.revenue)}</td>
      <td className="text-end" style={{ color: p.profit >= 0 ? "#198754" : "#dc3545" }}>{formatUSD(p.profit)}</td>
      <td className="text-center"><span style={{ fontSize: "0.75em", color: "#6b7280" }}>{p.status}</span></td>
    </tr>
  );

  return (
    <>
      {subtitle && <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div className="row mb-3">
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #dc3545" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Margen negativo</div>
              <div style={{ fontSize: "1.8em", fontWeight: 700, color: "#dc3545" }}>{negative.length}</div>
              <small className="text-muted">Proyectos con pérdida</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #ffc107" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Margen bajo (&lt; {MARGIN_ALERT_THRESHOLD}%)</div>
              <div style={{ fontSize: "1.8em", fontWeight: 700, color: "#ffc107" }}>{low.length}</div>
              <small className="text-muted">Requieren atención</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #198754" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Margen sano (≥ {MARGIN_ALERT_THRESHOLD}%)</div>
              <div style={{ fontSize: "1.8em", fontWeight: 700, color: "#198754" }}>{withMargin.length - negative.length - low.length}</div>
              <small className="text-muted">De {withMargin.length} proyectos con ingreso</small>
            </CardBody>
          </Card>
        </div>
      </div>

      {(negative.length + low.length) === 0 ? (
        <Alert color="success">Ningún proyecto con margen bajo o negativo. Todos están sobre el umbral del {MARGIN_ALERT_THRESHOLD}%.</Alert>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-hover">
            <thead className="table-light">
              <tr>
                <th style={{ width: 80 }}>Margen</th>
                <th>Proyecto</th>
                <th className="text-end">Ingreso</th>
                <th className="text-end">Utilidad</th>
                <th className="text-center" style={{ width: 100 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {negative.map((p) => renderRow(p, "neg"))}
              {low.map((p) => renderRow(p, "low"))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function MonedasDashboard({ byCurrency, subtitle }) {
  if (!byCurrency || byCurrency.length === 0) {
    return <Alert color="info" className="mt-3">No hay datos de exposición por moneda.</Alert>;
  }
  return (
    <>
      {subtitle && <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div style={{ overflowX: "auto" }}>
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Moneda</th>
              <th className="text-end">Ingreso</th>
              <th className="text-end">Costo</th>
              <th className="text-end">Utilidad</th>
              <th className="text-end" style={{ width: 100 }}>Margen</th>
            </tr>
          </thead>
          <tbody>
            {byCurrency.map((c) => {
              const margin = c.ingreso > 0 ? (c.utilidad / c.ingreso) * 100 : 0;
              return (
                <tr key={c.moneda}>
                  <td><strong>{c.moneda}</strong></td>
                  <td className="text-end">{c.moneda} {formatNum(c.ingreso)}</td>
                  <td className="text-end">{c.moneda} {formatNum(c.costo)}</td>
                  <td className="text-end" style={{ color: c.utilidad >= 0 ? "#198754" : "#dc3545" }}>{c.moneda} {formatNum(c.utilidad)}</td>
                  <td className="text-end">{formatPct(margin)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Alert color="light" className="mt-2" style={{ fontSize: "0.85em" }}>
        <strong>Nota:</strong> Los montos están en unidades de cada moneda (no convertidos a USD). Esto muestra la exposición cambiaria — cuánto del negocio se pacta en cada moneda antes de convertir. Los totales USD que ves en el resto de los reportes ya aplican los tipos de cambio (por partida cuando se capturaron, o la tabla mensual como fallback).
      </Alert>
    </>
  );
}

function YoYDashboard({ projects, subtitle }) {
  const byYearMonth = new Map();
  const years = new Set();
  (projects || []).forEach((p) => {
    const dateStr = p.inicio_date || p.registration_date;
    if (!dateStr) return;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    const year = d.getFullYear();
    const month = d.getMonth();
    years.add(year);
    const key = `${year}-${month}`;
    const bucket = byYearMonth.get(key) || { year, month, revenue: 0, count: 0 };
    bucket.revenue += Number(p.financials?.total_revenue) || 0;
    bucket.count += 1;
    byYearMonth.set(key, bucket);
  });

  const yearList = Array.from(years).sort();
  if (yearList.length < 2) {
    return <Alert color="info" className="mt-3">Se necesitan proyectos de al menos dos años para comparar año contra año. Actualmente solo hay datos de {yearList.join(", ") || "ningún año"}.</Alert>;
  }

  const monthLabels = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const datasets = yearList.map((year, i) => ({
    label: String(year),
    data: monthLabels.map((_, monthIdx) => (byYearMonth.get(`${year}-${monthIdx}`)?.revenue || 0)),
    backgroundColor: PALETTE[i % PALETTE.length],
    borderRadius: 3,
  }));

  const data = { labels: monthLabels, datasets };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      datalabels: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatUSD(ctx.raw)}` } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v) => formatUSD(v) } },
    },
  };

  const yearTotals = yearList.map((year) => {
    const total = monthLabels.reduce((acc, _, monthIdx) => acc + (byYearMonth.get(`${year}-${monthIdx}`)?.revenue || 0), 0);
    const count = monthLabels.reduce((acc, _, monthIdx) => acc + (byYearMonth.get(`${year}-${monthIdx}`)?.count || 0), 0);
    return { year, total, count };
  });

  return (
    <>
      {subtitle && <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div style={{ height: 340 }}><Bar data={data} options={options} /></div>
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <table className="table table-sm table-hover" style={{ fontSize: "0.9em" }}>
          <thead className="table-light">
            <tr>
              <th>Año</th>
              <th className="text-end">Proyectos</th>
              <th className="text-end">Ingreso total</th>
              <th className="text-end" style={{ width: 120 }}>Cambio YoY</th>
            </tr>
          </thead>
          <tbody>
            {yearTotals.map((y, i) => {
              const prev = i > 0 ? yearTotals[i - 1].total : null;
              const delta = prev && prev > 0 ? ((y.total - prev) / prev) * 100 : null;
              return (
                <tr key={y.year}>
                  <td><strong>{y.year}</strong></td>
                  <td className="text-end">{y.count}</td>
                  <td className="text-end">{formatUSD(y.total)}</td>
                  <td className="text-end" style={{ color: delta === null ? "#6b7280" : delta >= 0 ? "#198754" : "#dc3545" }}>
                    {delta === null ? "—" : `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// -------------------------- Rentabilidad por Producto --------------------------

function RentabilidadDashboard({ projects, subtitle }) {
  // Build per-product roll-up. Only include projects with revenue captured.
  const rows = [];
  const map = new Map();
  (projects || []).forEach((p) => {
    const rev = Number(p.financials?.total_revenue) || 0;
    if (rev <= 0) return;
    const key = (p.producto_nuevo || "").trim() || "Sin clasificar";
    const bucket = map.get(key) || { producto: key, count: 0, revenue: 0, profit: 0 };
    bucket.count += 1;
    bucket.revenue += rev;
    bucket.profit += Number(p.financials?.net_profit) || 0;
    map.set(key, bucket);
  });
  map.forEach((b) => {
    rows.push({ ...b, margin: b.revenue > 0 ? (b.profit / b.revenue) * 100 : 0 });
  });

  if (rows.length === 0) {
    return <Alert color="info" className="mt-3">No hay proyectos con ingreso capturado para calcular rentabilidad.</Alert>;
  }

  // Sort by margin desc — rentabilidad first.
  rows.sort((a, b) => b.margin - a.margin);

  const totalRevenue = rows.reduce((a, r) => a + r.revenue, 0);
  const totalProfit  = rows.reduce((a, r) => a + r.profit, 0);
  const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  // Bar chart: margen % by product, colored by profitability tier.
  const marginColor = (m) => m >= 50 ? "#198754" : m >= 20 ? "#0d6efd" : m >= 0 ? "#ffc107" : "#dc3545";
  const chartData = {
    labels: rows.map((r) => r.producto),
    datasets: [{
      label: "Margen %",
      data: rows.map((r) => r.margin),
      backgroundColor: rows.map((r) => marginColor(r.margin)),
      borderWidth: 0,
      borderRadius: 4,
    }],
  };
  const chartOpts = {
    indexAxis: "y",
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const r = rows[ctx.dataIndex];
            return [`Margen: ${r.margin.toFixed(1)}%`, `Ingreso: $${r.revenue.toLocaleString("en-US", {maximumFractionDigits: 0})}`, `Utilidad: $${r.profit.toLocaleString("en-US", {maximumFractionDigits: 0})}`];
          },
        },
      },
      datalabels: {
        anchor: "end", align: "end",
        color: "#374151", font: { size: 11, weight: 600 },
        formatter: (v) => `${v.toFixed(1)}%`,
      },
    },
    layout: { padding: { right: 60 } },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => `${v.toFixed(0)}%` } },
      y: { ticks: { font: { size: 12 } } },
    },
  };
  const chartHeight = Math.max(220, rows.length * 34 + 60);

  return (
    <>
      {subtitle && <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>{subtitle}</div>}

      <div className="row mb-3">
        <div className="col-md-4">
          <Card style={{ borderLeft: `4px solid ${marginColor(overallMargin)}` }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Margen agregado</div>
              <div style={{ fontSize: "1.8em", fontWeight: 700, color: marginColor(overallMargin) }}>{overallMargin.toFixed(1)}%</div>
              <small className="text-muted">De todos los productos con ingreso</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #198754" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Producto más rentable</div>
              <div style={{ fontSize: "1.4em", fontWeight: 700 }}>{rows[0].producto}</div>
              <small className="text-muted">Margen {rows[0].margin.toFixed(1)}%</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #dc3545" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Producto menos rentable</div>
              <div style={{ fontSize: "1.4em", fontWeight: 700 }}>{rows[rows.length - 1].producto}</div>
              <small className="text-muted">Margen {rows[rows.length - 1].margin.toFixed(1)}%</small>
            </CardBody>
          </Card>
        </div>
      </div>

      <div style={{ height: chartHeight }}>
        <Bar data={chartData} options={chartOpts} />
      </div>

      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <table className="table table-sm table-hover" style={{ fontSize: "0.9em" }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Producto</th>
              <th className="text-end">Proyectos</th>
              <th className="text-end">Ingreso</th>
              <th className="text-end">Utilidad</th>
              <th className="text-end" style={{ width: 90 }}>Margen</th>
              <th className="text-end" style={{ width: 100 }}>% Ingreso Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.producto}>
                <td className="text-muted">{i + 1}</td>
                <td><strong>{r.producto}</strong></td>
                <td className="text-end">{r.count}</td>
                <td className="text-end">${r.revenue.toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                <td className="text-end" style={{ color: r.profit >= 0 ? "#198754" : "#dc3545" }}>
                  ${r.profit.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                </td>
                <td className="text-end" style={{ color: marginColor(r.margin), fontWeight: 600 }}>
                  {r.margin.toFixed(1)}%
                </td>
                <td className="text-end">{totalRevenue > 0 ? ((r.revenue / totalRevenue) * 100).toFixed(1) : "0"}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="table-light">
              <td></td>
              <td><strong>Total</strong></td>
              <td className="text-end"><strong>{rows.reduce((a, r) => a + r.count, 0)}</strong></td>
              <td className="text-end"><strong>${totalRevenue.toLocaleString("en-US", { maximumFractionDigits: 0 })}</strong></td>
              <td className="text-end" style={{ color: totalProfit >= 0 ? "#198754" : "#dc3545" }}><strong>${totalProfit.toLocaleString("en-US", { maximumFractionDigits: 0 })}</strong></td>
              <td className="text-end" style={{ color: marginColor(overallMargin), fontWeight: 600 }}><strong>{overallMargin.toFixed(1)}%</strong></td>
              <td className="text-end"><strong>100%</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Alert color="light" className="mt-3" style={{ fontSize: "0.85em" }}>
        <strong>Cómo leer este reporte:</strong> ordenado del producto más rentable al menos rentable (por margen %, no por ingreso). Colores: <span style={{ color: "#198754", fontWeight: 600 }}>verde ≥ 50%</span>, <span style={{ color: "#0d6efd", fontWeight: 600 }}>azul ≥ 20%</span>, <span style={{ color: "#ffc107", fontWeight: 600 }}>amarillo ≥ 0%</span>, <span style={{ color: "#dc3545", fontWeight: 600 }}>rojo si el producto pierde dinero</span>. Un producto de alto ingreso pero margen bajo puede estar consumiendo más recursos de los que aporta; uno de bajo ingreso pero alto margen podría ser una oportunidad de escalar.
      </Alert>
    </>
  );
}

// ---------------------------- Weekly + AI ----------------------------

function fmtPctChange(v) {
  if (v === null || v === undefined) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}

function KpiCard({ label, valueMain, valueSub, deltaText, deltaColor, borderColor }) {
  return (
    <Card style={{ borderLeft: `4px solid ${borderColor}`, height: "100%" }}>
      <CardBody>
        <div className="text-muted" style={{ fontSize: "0.7em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
          {label}
        </div>
        <div style={{ fontSize: "1.5em", fontWeight: 700, color: "#111827", marginTop: 6 }}>
          {valueMain}
        </div>
        {valueSub && <div className="text-muted" style={{ fontSize: "0.85em" }}>{valueSub}</div>}
        {deltaText && (
          <div style={{ fontSize: "0.85em", color: deltaColor || "#6b7280", fontWeight: 600, marginTop: 4 }}>
            {deltaText} vs semana anterior
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function WeeklyProjectList({ title, projects, emptyText }) {
  if (!projects || projects.length === 0) {
    return (
      <div className="mb-3">
        <h6>{title}</h6>
        <div className="text-muted" style={{ fontSize: "0.9em" }}>{emptyText}</div>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <h6 className="mb-2">{title} <Badge color="light" pill>{projects.length}</Badge></h6>
      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm table-hover" style={{ fontSize: "0.85em", marginBottom: 0 }}>
          <thead className="table-light">
            <tr>
              <th>Proyecto</th>
              <th>Cliente</th>
              <th>Líder</th>
              <th className="text-end">Ingreso</th>
              <th className="text-end" style={{ width: 90 }}>Margen</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.proyecto_code}>
                <td>
                  <strong>{p.name}</strong>
                  <div className="text-muted" style={{ fontSize: "0.8em" }}>{p.proyecto_code}</div>
                </td>
                <td>{p.cliente || "—"}</td>
                <td>{p.lider || "—"}</td>
                <td className="text-end">${Number(p.revenue).toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                <td className="text-end" style={{ color: p.margin >= 20 ? "#198754" : p.margin >= 0 ? "#ffc107" : "#dc3545" }}>
                  {p.margin.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WeeklyDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  const [analysisAt, setAnalysisAt] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const year = summary?.current?.year;
  const week = summary?.current?.iso_week;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const headers = getAuthHeaders();
        const { data } = await axios.get(API_URL_BI_WEEKLY_SUMMARY, { headers });
        if (cancelled) return;
        setSummary(data);
      } catch (e) {
        if (cancelled) return;
        setError("No pudimos cargar el resumen semanal.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When summary lands, try to read a cached analysis for that week.
  useEffect(() => {
    if (!year || !week) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = getAuthHeaders();
        const { data } = await axios.get(API_URL_BI_WEEKLY_ANALYSIS, {
          headers, params: { year, week },
        });
        if (cancelled) return;
        if (data?.cached && data?.analysis) {
          setAnalysis(data.analysis);
          setAnalysisAt(data.generated_at);
        } else {
          setAnalysis(null); setAnalysisAt(null);
        }
      } catch (_) {
        // Silent — the user can still generate on demand.
      }
    })();
    return () => { cancelled = true; };
  }, [year, week]);

  const runAnalysis = async (force = false) => {
    if (!year || !week) return;
    setAnalysisLoading(true); setAnalysisError(null);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        API_URL_BI_WEEKLY_ANALYSIS,
        { year, week, force },
        { headers }
      );
      setAnalysis(data.analysis);
      setAnalysisAt(data.generated_at);
    } catch (e) {
      const detail = e?.response?.data?.error || e.message || "Error desconocido.";
      setAnalysisError(detail);
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (loading) return <div style={{ padding: 20 }}><Spinner /> Cargando resumen semanal…</div>;
  if (error) return <Alert color="danger">{error}</Alert>;
  if (!summary) return null;

  const { current, previous, deltas } = summary;

  const deltaColor = (v) => v === null || v === undefined ? "#6b7280"
    : v > 0 ? "#198754" : v < 0 ? "#dc3545" : "#6b7280";

  const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  return (
    <>
      <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>
        Semana ISO <strong>{current.year}-W{String(current.iso_week).padStart(2, "0")}</strong>{" "}
        ({current.week_start} al {current.week_end}), comparada con la semana anterior
        ({previous.week_start} al {previous.week_end}).
      </div>

      <div className="row mb-3">
        <div className="col-md-3 mb-3">
          <KpiCard
            label="Proyectos iniciados"
            valueMain={current.started.count}
            valueSub={`${fmtUSD(current.started.revenue)} comprometido`}
            deltaText={fmtPctChange(deltas.started_revenue_pct)}
            deltaColor={deltaColor(deltas.started_revenue_pct)}
            borderColor="#0d6efd"
          />
        </div>
        <div className="col-md-3 mb-3">
          <KpiCard
            label="Utilidad iniciada"
            valueMain={fmtUSD(current.started.profit)}
            valueSub={`Margen ${current.started.margin.toFixed(1)}%`}
            deltaText={fmtPctChange(deltas.started_profit_pct)}
            deltaColor={deltaColor(deltas.started_profit_pct)}
            borderColor="#198754"
          />
        </div>
        <div className="col-md-3 mb-3">
          <KpiCard
            label="Proyectos cerrados"
            valueMain={current.ended.count}
            valueSub={`${fmtUSD(current.ended.revenue)} entregado`}
            deltaText={fmtPctChange(deltas.ended_revenue_pct)}
            deltaColor={deltaColor(deltas.ended_revenue_pct)}
            borderColor="#6f42c1"
          />
        </div>
        <div className="col-md-3 mb-3">
          <KpiCard
            label="Alertas nuevas"
            valueMain={current.low_margin_alerts.length}
            valueSub="Proyectos iniciados con margen < 20%"
            borderColor="#dc3545"
          />
        </div>
      </div>

      <WeeklyProjectList
        title="Proyectos que iniciaron esta semana"
        projects={current.started.projects}
        emptyText="Ningún proyecto arrancó esta semana."
      />

      <WeeklyProjectList
        title="Proyectos que cerraron esta semana"
        projects={current.ended.projects}
        emptyText="Ningún proyecto se terminó esta semana."
      />

      {current.low_margin_alerts.length > 0 && (
        <WeeklyProjectList
          title="Alertas — proyectos iniciados con margen bajo"
          projects={current.low_margin_alerts}
          emptyText=""
        />
      )}

      <Card className="mt-4" style={{ borderLeft: "4px solid #6f42c1" }}>
        <CardHeader style={{ backgroundColor: "#faf7ff" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
            <strong>Análisis Ejecutivo</strong>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {analysisAt && (
                <small className="text-muted">
                  Generado {new Date(analysisAt).toLocaleString("es-MX")}
                </small>
              )}
              <Button
                size="sm"
                color="primary"
                disabled={analysisLoading || !year}
                onClick={() => runAnalysis(!!analysis)}
              >
                {analysisLoading
                  ? "Generando…"
                  : analysis ? "Regenerar" : "Generar análisis"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {analysisError && <Alert color="danger">{analysisError}</Alert>}
          {analysis ? (
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: "0.95em", color: "#111827" }}>
              {analysis}
            </div>
          ) : (
            <div className="text-muted" style={{ fontSize: "0.9em" }}>
              Todavía no hay análisis generado para esta semana. Haz clic en <strong>Generar análisis</strong> para producir un resumen ejecutivo interpretando los números de arriba. El análisis se guarda para que próximas visitas lo carguen instantáneamente.
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function NuevosVsRepetidoresDashboard({ projects, subtitle }) {
  const clientProjects = new Map();
  (projects || []).forEach((p) => {
    const cliente = (p.cliente || "").trim() || "Sin cliente";
    const dateStr = p.inicio_date || p.registration_date;
    const date = dateStr ? new Date(dateStr) : null;
    if (!clientProjects.has(cliente)) clientProjects.set(cliente, []);
    clientProjects.get(cliente).push({
      code: p.proyecto_code,
      name: p.name,
      date: date && !isNaN(date.getTime()) ? date : null,
      revenue: Number(p.financials?.total_revenue) || 0,
    });
  });

  let nuevoRevenue = 0, nuevoCount = 0, repetidorRevenue = 0, repetidorCount = 0;
  const clientSummary = [];

  clientProjects.forEach((projs, cliente) => {
    const sorted = projs.slice().sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
    const firstProjectRevenue = sorted[0]?.revenue || 0;
    const laterRevenue = sorted.slice(1).reduce((a, r) => a + r.revenue, 0);
    nuevoRevenue += firstProjectRevenue;
    nuevoCount += 1;
    repetidorRevenue += laterRevenue;
    repetidorCount += sorted.length - 1;
    clientSummary.push({
      cliente,
      projects: sorted.length,
      total: sorted.reduce((a, r) => a + r.revenue, 0),
      isReturning: sorted.length > 1,
    });
  });

  const totalRevenue = nuevoRevenue + repetidorRevenue;
  const returningClients = clientSummary.filter((c) => c.isReturning);
  const returningPct = clientProjects.size > 0 ? (returningClients.length / clientProjects.size) * 100 : 0;

  if (clientProjects.size === 0) {
    return <Alert color="info" className="mt-3">No hay proyectos con cliente capturado.</Alert>;
  }

  return (
    <>
      {subtitle && <div className="text-muted mb-3" style={{ fontSize: "0.9em" }}>{subtitle}</div>}
      <div className="row mb-3">
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #0d6efd" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Clientes únicos</div>
              <div style={{ fontSize: "1.8em", fontWeight: 700 }}>{clientProjects.size}</div>
              <small className="text-muted">{returningClients.length} han vuelto ({formatPct(returningPct)})</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #198754" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Ingreso de clientes nuevos</div>
              <div style={{ fontSize: "1.5em", fontWeight: 700, color: "#198754" }}>{formatUSD(nuevoRevenue)}</div>
              <small className="text-muted">{nuevoCount} proyectos · primer contrato de cada cliente</small>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ borderLeft: "4px solid #6f42c1" }}>
            <CardBody>
              <div className="text-muted" style={{ fontSize: "0.75em", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Ingreso de clientes repetidores</div>
              <div style={{ fontSize: "1.5em", fontWeight: 700, color: "#6f42c1" }}>{formatUSD(repetidorRevenue)}</div>
              <small className="text-muted">{repetidorCount} proyectos · segundo contrato en adelante</small>
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="text-muted mb-2" style={{ fontSize: "0.85em" }}>
        <strong>{formatPct(totalRevenue > 0 ? (repetidorRevenue / totalRevenue) * 100 : 0)}</strong> del ingreso total viene de clientes que ya habían comprado antes.
      </div>

      <h6 className="mt-3 mb-2">Clientes con más proyectos</h6>
      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm table-hover" style={{ fontSize: "0.9em" }}>
          <thead className="table-light">
            <tr>
              <th>Cliente</th>
              <th className="text-end" style={{ width: 100 }}>Proyectos</th>
              <th className="text-end">Ingreso total</th>
              <th className="text-center" style={{ width: 120 }}>Tipo</th>
            </tr>
          </thead>
          <tbody>
            {clientSummary
              .sort((a, b) => b.projects - a.projects || b.total - a.total)
              .slice(0, 20)
              .map((c) => (
                <tr key={c.cliente}>
                  <td><strong>{c.cliente}</strong></td>
                  <td className="text-end">{c.projects}</td>
                  <td className="text-end">{formatUSD(c.total)}</td>
                  <td className="text-center">
                    <Badge color={c.isReturning ? "primary" : "secondary"}>
                      {c.isReturning ? "Repetidor" : "Nuevo"}
                    </Badge>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function Reportes() {
  const [active, setActive] = useState(REPORTS[0].id);
  const [geoLevel, setGeoLevel] = useState(GEO_LEVELS[0].id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [utilidades, setUtilidades] = useState([]);
  const [currencyExposure, setCurrencyExposure] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = getAuthHeaders();
        const params = { start_date: "2020-01-01T00:00:00", end_date: "2100-01-01T00:00:00" };
        const [projectsRes, instructorsRes, utilidadesRes, currencyRes] = await Promise.all([
          axios.get(API_URL_BI_PROJECTS, { headers, params }),
          axios.get(API_URL_instructores, { headers }).catch(() => ({ data: [] })),
          axios.get(API_URL_utilidades_partners, { headers }).catch(() => ({ data: [] })),
          axios.get(API_URL_BI_CURRENCY_EXPOSURE, { headers }).catch(() => ({ data: { by_currency: [] } })),
        ]);
        if (cancelled) return;
        setProjects(projectsRes.data?.projects || []);
        setSummary(projectsRes.data?.summary || null);
        setInstructors(Array.isArray(instructorsRes.data) ? instructorsRes.data : []);
        setUtilidades(Array.isArray(utilidadesRes.data) ? utilidadesRes.data : []);
        setCurrencyExposure(currencyRes.data?.by_currency || []);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError("No pudimos cargar los datos. Verifica tu sesión e intenta de nuevo.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filters — Fecha default en "Año a la fecha" para que la primera vista
  // sea del año en curso, no de todo el histórico.
  const [datePreset, setDatePreset] = useState("ytd");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [fSede,     setFSede]     = useState("all");
  const [fPais,     setFPais]     = useState("all");
  const [fProducto, setFProducto] = useState("all");
  const [fSubproducto, setFSubproducto] = useState("all");
  const [fStatus,   setFStatus]   = useState("all");
  const [fLider,    setFLider]    = useState("all");

  const dateRange = useMemo(
    () => resolveDateRange(datePreset, customStart, customEnd),
    [datePreset, customStart, customEnd]
  );

  const filters = { sede: fSede, pais: fPais, producto: fProducto, subproducto: fSubproducto, status: fStatus, lider: fLider };
  const filtersActive = !!dateRange
    || fSede !== "all" || fPais !== "all" || fProducto !== "all" || fSubproducto !== "all"
    || fStatus !== "all" || fLider !== "all";

  const filteredProjects = useMemo(
    () => (projects || []).filter((p) => projectMatchesFilters(p, filters, dateRange)),
    [projects, fSede, fPais, fProducto, fSubproducto, fStatus, fLider, dateRange]
  );
  const filteredCodes = useMemo(
    () => new Set(filteredProjects.map((p) => p.proyecto_code)),
    [filteredProjects]
  );
  const filteredInstructors = useMemo(
    () => filtersActive ? (instructors || []).filter((i) => filteredCodes.has(i.proyecto_code)) : (instructors || []),
    [instructors, filteredCodes, filtersActive]
  );
  const filteredUtilidades = useMemo(
    () => filtersActive ? (utilidades || []).filter((u) => filteredCodes.has(u.proyecto_code)) : (utilidades || []),
    [utilidades, filteredCodes, filtersActive]
  );

  // Options for dropdowns — always derived from the FULL projects list so the
  // user can widen a filter even when the current filtered set is small.
  const sedeOpts     = useMemo(() => uniqueValues(projects, (p) => p.sede?.name), [projects]);
  const paisOpts     = useMemo(() => uniqueValues(projects, (p) => p.pais), [projects]);
  // Producto / Subproducto vienen de la clasificación nueva (5 niveles). Un
  // proyecto sin clasificar cae en "Sin clasificar" hasta que LSSI lo revise.
  const productoOpts = useMemo(() => uniqueValues(projects, (p) => p.producto_nuevo), [projects]);
  const subproductoOpts = useMemo(
    () => uniqueValues(projects.filter((p) => fProducto === "all" || (p.producto_nuevo || "") === fProducto), (p) => p.subproducto),
    [projects, fProducto]
  );
  const statusOpts   = useMemo(() => uniqueValues(projects, (p) => p.status), [projects]);
  const liderOpts    = useMemo(() => uniqueValues(projects, (p) => p.lider), [projects]);

  const resetFilters = () => {
    setDatePreset("all"); setCustomStart(""); setCustomEnd("");
    setFSede("all"); setFPais("all"); setFProducto("all"); setFSubproducto("all");
    setFStatus("all"); setFLider("all");
  };

  const totalRevenue = useMemo(
    () => filteredProjects.reduce((a, p) => a + (p.financials?.total_revenue || 0), 0),
    [filteredProjects]
  );

  const productoRows  = useMemo(() => aggregate(filteredProjects, (p) => p.producto_nuevo || "Sin clasificar"), [filteredProjects]);
  const modalidadRows = useMemo(() => aggregate(filteredProjects, (p) => {
    if (!p.modalidad_nueva && !p.tipo) return p.producto_nuevo ? "Sin modalidad" : "Sin clasificar";
    return [p.modalidad_nueva, p.tipo].filter(Boolean).join(" · ");
  }), [filteredProjects]);
  const sedeRows      = useMemo(() => aggregate(filteredProjects, (p) => p.sede?.name), [filteredProjects]);
  const paisRows      = useMemo(() => aggregate(filteredProjects, (p) => p.pais), [filteredProjects]);
  const ciudadRows    = useMemo(() => aggregate(filteredProjects, (p) => {
    const city = (p.ciudad || "").trim() || "Sin especificar";
    const country = (p.pais || "").trim();
    return country ? `${city} · ${country}` : city;
  }), [filteredProjects]);
  const clientesFull  = useMemo(() => aggregate(filteredProjects, (p) => p.cliente), [filteredProjects]);
  const clientesRows  = useMemo(() => clientesFull.slice(0, TOP_N_CLIENTES), [clientesFull]);
  const months        = useMemo(() => bucketByMonth(filteredProjects, "inicio_date"), [filteredProjects]);

  const instructorRows = useMemo(() => {
    const rows = aggregate(
      filteredInstructors,
      (i) => i.instructor,
      (i) => (Number(i.horas) || 0) * (Number(i.semanas) || 0)
    );
    return rows.slice(0, TOP_N_INSTRUCTORES);
  }, [filteredInstructors]);
  const totalHoras = useMemo(
    () => (filteredInstructors || []).reduce((a, i) => a + (Number(i.horas) || 0) * (Number(i.semanas) || 0), 0),
    [filteredInstructors]
  );

  const partnerRows = useMemo(() => {
    // El endpoint /api/utilidades-api/ agrega por (partner, proyecto_code) y
    // expone la suma como `total_cantidad` — no como `cantidad`. Confundir
    // los dos hace que todo salga en $0. También filtramos filas con partner
    // vacío o con nombres inválidos ("0", "-", etc.) para no ensuciar el
    // ranking con placeholders.
    const isValidPartner = (name) => {
      const n = (name || "").trim();
      if (!n) return false;
      if (EXCLUDED_PARTNERS_FROM_RANKING.has(n)) return false;
      if (/^[0\-.\s]+$/.test(n)) return false; // "0", "-", "0.0", etc.
      return true;
    };
    const rows = aggregate(
      (filteredUtilidades || []).filter((u) => isValidPartner(u.partner)),
      (u) => u.partner,
      (u) => Number(u.total_cantidad ?? u.cantidad) || 0
    );
    return rows.slice(0, TOP_N_PARTNERS);
  }, [filteredUtilidades]);
  const totalPartnerRevenue = useMemo(
    () => partnerRows.reduce((a, r) => a + r.value, 0),
    [partnerRows]
  );

  const geoRows = geoLevel === "sede" ? sedeRows : geoLevel === "pais" ? paisRows : ciudadRows;

  const top5Concentration = clientesFull.length > 0
    ? (clientesFull.slice(0, 5).reduce((a, r) => a + r.value, 0) / totalRevenue) * 100
    : 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 className="mb-1">Reportes</h2>
          <small className="text-muted">
            Reportes analíticos nativos dentro de LSSI Fin. Ingresos totales en USD sumando los proyectos que pasan los filtros aplicados.
          </small>
        </div>
        {!loading && !error && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.75em", letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", fontWeight: 600 }}>Ingreso total</div>
            <div style={{ fontSize: "1.4em", fontWeight: 700, color: "#111827" }}>{formatUSD(totalRevenue)}</div>
            <small className="text-muted">
              {filtersActive
                ? <>Mostrando {filteredProjects.length} de {projects.length} proyectos</>
                : <>{projects.length} proyecto{projects.length === 1 ? "" : "s"}</>}
            </small>
          </div>
        )}
      </div>

      {!loading && !error && (
        <Card className="mb-3" style={{ backgroundColor: "#f8fafc" }}>
          <CardBody style={{ padding: "12px 16px" }}>
            <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
              <div style={{ minWidth: 170 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Fecha</Label>
                <Input bsSize="sm" type="select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                  {DATE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </Input>
              </div>
              {datePreset === "custom" && (
                <>
                  <div style={{ minWidth: 150 }}>
                    <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Desde</Label>
                    <Input bsSize="sm" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                  </div>
                  <div style={{ minWidth: 150 }}>
                    <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Hasta</Label>
                    <Input bsSize="sm" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                  </div>
                </>
              )}
              <div style={{ minWidth: 170 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Value Stream</Label>
                <Input bsSize="sm" type="select" value={fSede} onChange={(e) => setFSede(e.target.value)}>
                  <option value="all">Todos</option>
                  {sedeOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div style={{ minWidth: 150 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>País</Label>
                <Input bsSize="sm" type="select" value={fPais} onChange={(e) => setFPais(e.target.value)}>
                  <option value="all">Todos</option>
                  {paisOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div style={{ minWidth: 150 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Producto</Label>
                <Input bsSize="sm" type="select" value={fProducto} onChange={(e) => { setFProducto(e.target.value); setFSubproducto("all"); }}>
                  <option value="all">Todos</option>
                  {productoOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div style={{ minWidth: 170 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Subproducto</Label>
                <Input bsSize="sm" type="select" value={fSubproducto} onChange={(e) => setFSubproducto(e.target.value)}>
                  <option value="all">Todos</option>
                  {subproductoOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div style={{ minWidth: 130 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Status</Label>
                <Input bsSize="sm" type="select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="all">Todos</option>
                  {statusOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div style={{ minWidth: 150 }}>
                <Label className="mb-1" style={{ fontSize: "0.75em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>Líder</Label>
                <Input bsSize="sm" type="select" value={fLider} onChange={(e) => setFLider(e.target.value)}>
                  <option value="all">Todos</option>
                  {liderOpts.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </div>
              <div>
                <Button
                  size="sm"
                  color={filtersActive ? "danger" : "secondary"}
                  outline
                  disabled={!filtersActive}
                  onClick={resetFilters}
                >
                  Limpiar filtros
                </Button>
              </div>
            </div>
            {filtersActive && (
              <div className="mt-2 text-muted" style={{ fontSize: "0.8em" }}>
                Filtros activos. Los reportes de <em>Semanal</em> y <em>Monedas</em> muestran datos globales (no aplican filtros de proyecto todavía).
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Nav tabs className="mb-3" style={{ flexWrap: "wrap" }}>
        {REPORTS.map((r) => (
          <NavItem key={r.id}>
            <NavLink href="#" active={active === r.id} onClick={(e) => { e.preventDefault(); setActive(r.id); }}>
              {r.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {loading && (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spinner /> <div className="mt-2 text-muted">Cargando datos…</div>
        </div>
      )}

      {error && <Alert color="danger">{error}</Alert>}

      {!loading && !error && (
        <Card>
          <CardHeader style={{ backgroundColor: "#fafafa" }}>
            <strong>
              {active === "semanal"        && "Resumen Semanal"}
              {active === "producto"       && "Ventas por Producto"}
              {active === "rentabilidad"   && "Rentabilidad por Producto"}
              {active === "modalidad"      && "Ventas por Modalidad"}
              {active === "geografia"      && "Ventas por Geolocalización"}
              {active === "clientes"       && `Top ${TOP_N_CLIENTES} Clientes por Ingreso`}
              {active === "mes-a-mes"      && "Ingreso, Utilidad y Margen — Mes a Mes"}
              {active === "margen-alertas" && "Margen y Alertas de Proyectos"}
              {active === "instructores"   && `Top ${TOP_N_INSTRUCTORES} Instructores por Horas`}
              {active === "partners"       && `Utilidad por Partner (Top ${TOP_N_PARTNERS})`}
              {active === "monedas"        && "Exposición por Moneda"}
              {active === "anio-vs-anio"   && "Comparativa Año contra Año"}
              {active === "repetidores"    && "Clientes Nuevos vs Repetidores"}
            </strong>
            {active === "geografia" && (
              <div className="mt-2 d-flex gap-2 flex-wrap">
                {GEO_LEVELS.map((lvl) => (
                  <button key={lvl.id} type="button"
                    className={`btn btn-sm ${geoLevel === lvl.id ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setGeoLevel(lvl.id)}>
                    {lvl.label}
                  </button>
                ))}
              </div>
            )}
            {active === "clientes" && clientesFull.length > 5 && (
              <div className="mt-2 text-muted" style={{ fontSize: "0.85em" }}>
                Los 5 clientes más grandes concentran el <strong>{formatPct(top5Concentration)}</strong> del ingreso total ({clientesFull.length} clientes distintos en total).
              </div>
            )}
          </CardHeader>
          <CardBody>
            {active === "semanal" && (
              <WeeklyDashboard />
            )}
            {active === "producto" && (
              <BarDashboard
                subtitle="Ingreso acumulado por Producto de la clasificación nueva (Cursos, Talleres, Proyectos, Coaching, Certificación, Libros, Tecnología). Usa el filtro Subproducto para bajar a YB, GB, Hoshin Kanri, etc."
                rows={productoRows} total={totalRevenue}
              />
            )}
            {active === "rentabilidad" && (
              <RentabilidadDashboard
                subtitle="Producto ordenado del más rentable al menos rentable (por margen %). Solo cuenta proyectos con ingreso capturado."
                projects={filteredProjects}
              />
            )}
            {active === "modalidad" && (
              <BarDashboard
                subtitle="Ingreso acumulado por Modalidad y Tipo de la clasificación nueva (Presencial, Virtual en vivo, Híbrido, Online × Público / Privado)."
                rows={modalidadRows} total={totalRevenue} seed={2}
              />
            )}
            {active === "geografia" && (
              <BarDashboard
                subtitle={`Ingreso agrupado por ${GEO_LEVELS.find((l) => l.id === geoLevel)?.label.toLowerCase()}.`}
                rows={geoRows} total={totalRevenue} seed={4}
              />
            )}
            {active === "clientes" && (
              <BarDashboard
                subtitle={`Los ${TOP_N_CLIENTES} clientes con mayor ingreso acumulado.`}
                rows={clientesRows} total={totalRevenue} seed={6}
              />
            )}
            {active === "mes-a-mes" && (
              <TrendDashboard
                subtitle="Ingreso y utilidad (barras, USD) y margen % (línea) por mes, usando la fecha de inicio de cada proyecto."
                months={months}
              />
            )}
            {active === "margen-alertas" && (
              <AlertsDashboard
                subtitle={`Proyectos con margen bajo el umbral de ${MARGIN_ALERT_THRESHOLD}%, del más bajo al más alto.`}
                projects={filteredProjects}
              />
            )}
            {active === "instructores" && (
              <BarDashboard
                subtitle={`Ranking por total de horas capturadas (horas × semanas sumadas de todos los proyectos donde participa). El ingreso en USD por instructor requiere convertir por moneda y va en una tanda futura.`}
                rows={instructorRows} total={totalHoras} seed={5}
                valueLabel="Horas totales" valueFormatter={formatNum}
                countLabel="Registros" seriesLabel="Horas totales"
              />
            )}
            {active === "partners" && (
              <BarDashboard
                subtitle="Utilidad acumulada por partner (en USD), tomada de las asignaciones registradas en el módulo de Utilidades. Excluye LSSI y LSSI Europa."
                rows={partnerRows} total={totalPartnerRevenue} seed={3}
                valueLabel="Utilidad (USD)" valueFormatter={formatUSD}
                countLabel="Asignaciones" seriesLabel="Utilidad (USD)"
              />
            )}
            {active === "monedas" && (
              <MonedasDashboard
                subtitle="Exposición del negocio por moneda — cuánto se factura y cuánto se paga en cada divisa, en sus unidades originales antes de convertir a USD."
                byCurrency={currencyExposure}
              />
            )}
            {active === "anio-vs-anio" && (
              <YoYDashboard
                subtitle="Ingreso mensual comparado entre años, usando la fecha de inicio de cada proyecto. El cambio YoY del año más reciente contra el anterior aparece en la tabla."
                projects={filteredProjects}
              />
            )}
            {active === "repetidores" && (
              <NuevosVsRepetidoresDashboard
                subtitle="El primer proyecto de cada cliente cuenta como cliente nuevo; los subsiguientes cuentan como cliente repetidor. Ordenado por fecha de inicio."
                projects={filteredProjects}
              />
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
